<template>
  <v-menu v-model="menu" :close-on-content-click="false" location="bottom right">
    <template #activator="{ props: _props }">
      <v-btn v-bind="_props" height="auto" class="mr-2" border
        >{{ title }}
        <template v-if="!amountModel" #append>
          {{ currencySymbol }}
        </template>
      </v-btn>
    </template>
    <v-card width="300">
      <v-form ref="form" @submit.prevent="apply">
        <v-card-text>
          <v-text-field
            v-model.number="min"
            hide-details="auto"
            :label="t('amount.minAmount')"
            type="number"
            :rules="[required, nonnegativeNumber]"
          >
            <template #append-inner>{{ currencySymbol }}</template>
          </v-text-field>
          <v-text-field
            v-model.number="max"
            hide-details="auto"
            class="mt-3"
            :label="t('amount.maxAmount')"
            type="number"
            :rules="[required, nonnegativeNumber, maxGreaterThanMin]"
          >
            <template #append-inner>{{ currencySymbol }}</template>
          </v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="
              amountModel = null;
              menu = false;
            "
            >{{ t("common.clear") }}</v-btn
          >
          <v-btn type="submit">{{ t("common.apply") }}</v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-menu>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { useRules } from "@/composables/useRules";
import { formatCurrency } from "@/utils/tools";
import { computed, ref, watch } from "vue";
import type { VForm } from "vuetify/components";

const props = defineProps<{
  currencySymbol: string;
  currencyCode: string | null;
}>();

const { t, locale } = useI18n();
const { required, nonnegativeNumber } = useRules();

const maxGreaterThanMin = (maxValue: unknown) => {
  if (typeof maxValue !== "number" || min.value === null) return true;
  return maxValue >= min.value || t("errors.maxMustBeGreaterThanMin");
};

const form = ref<VForm | null>(null);

type Amount = { min: number; max: number };

const amountModel = defineModel<Amount | null>({
  required: true,
});

const min = ref<number | null>(null);
const max = ref<number | null>(null);

watch(amountModel, () => {
  init();
});

const apply = async () => {
  const validateForm = await form.value?.validate();
  if (!validateForm?.valid) return;
  if (min.value === null || max.value === null) return;

  amountModel.value = {
    min: min.value,
    max: max.value,
  };

  menu.value = false;
};

const menu = ref(false);

watch(menu, (isOpen) => {
  if (!isOpen) {
    init();
  }
});

watch(min, (value, old) => {
  if (max.value === null || max.value === old) {
    max.value = value;
  }

  void form.value?.validate();
});

const init = () => {
  if (amountModel.value) {
    min.value = amountModel.value.min;
    max.value = amountModel.value.max;
  } else {
    min.value = null;
    max.value = null;
  }
};

const title = computed(() => {
  if (amountModel.value && props.currencyCode) {
    return `${formatCurrency({
      value: amountModel.value.min,
      currencyCode: props.currencyCode,
      locale: locale.value,
    })} - ${formatCurrency({
      value: amountModel.value.max,
      currencyCode: props.currencyCode,
      locale: locale.value,
    })}`;
  }

  return t("common.amount");
});
</script>
